import * as React from 'react';
import { PopupHeader } from '../../../../../common/components/PopupHeader/PopupHeader';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { RestApi } from '../../../../../common/services/rest-api.service';
import { Common } from '../../../../../common/services/common.service';
import { getRows } from '../../../../../features/GridSlice';
import { showError, showSuccess } from '../../../../../features/AlertSlice';
import { useDispatch } from 'react-redux';
import { AppLoading } from '../../../../../common/components/appLoading/appLoading';
import './BulkDeleteRemovedAssetsPopup.scss';

const ASSET_TYPES = [
  'Managed Domain',
  'Subdomain',
  'Suffix',
  'Domain',
  'IP Range'
];

export function BulkDeleteRemovedAssetsPopup(props: any) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [assetNames, setAssetNames] = useState<string>('');
  const [assetType, setAssetType] = useState<string>('');
  const [inputError, setInputError] = useState<boolean>(false);
  const [typeError, setTypeError] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const dispatch = useDispatch<any>();

  useEffect(() => {
    setInputError(false);
    setErrorText('');
  }, [assetNames]);

  useEffect(() => {
    setTypeError(false);
  }, [assetType]);

  const validate = (): boolean => {
    let hasError = false;

    if (!assetNames.trim()) {
      setInputError(true);
      setErrorText('Asset names are required');
      hasError = true;
    }

    if (!assetType) {
      setTypeError(true);
      hasError = true;
    }

    return !hasError;
  };

  const submit = () => {
    if (!validate()) {
      return;
    }

    setLoading(true);
    const url = Common.AddMsspAccountIfNeeded('settings/asset-management/removed-assets/bulk-delete/');

    // Split textarea content by lines and filter out empty lines
    const assetList = assetNames
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    const payload = {
      assets: assetList.map(name => ({ name, type: assetType })),
      reason: 'removed_by_customer' // Default reason for bulk delete
    };

    RestApi.setData(url, payload, 'POST').subscribe(
      (response: any) => {
        setLoading(false);
        props.onClose();
        dispatch(getRows());
        dispatch(showSuccess(`${assetList.length} assets restored successfully`));
      },
      (error: any) => {
        setLoading(false);
        dispatch(showError('Failed to restore assets'));
      }
    );
  };

  return (
    <div className="BulkDeleteRemovedAssetsPopup popup-inner-wrapper">
      <PopupHeader text="Bulk Restore Assets" onClose={props.onClose} />
      <div className="popup-inner">
        <div className="message">
          <div>Restore multiple assets and remove from the Removed assets list.</div>
          <div>Each asset name should appear in a separate line.</div>
        </div>

        <div className="form-field">
          <label htmlFor="asset-names">Asset Names *</label>
          <textarea
            id="asset-names"
            className={classNames('form-textarea', { error: inputError })}
            value={assetNames}
            onChange={(e) => setAssetNames(e.target.value)}
            placeholder="Enter asset names, one per line"
            rows={8}
          />
          {inputError && <div className="error-text">{errorText}</div>}
        </div>

        <div className="form-field">
          <FormControl fullWidth error={typeError}>
            <InputLabel id="asset-type-label">Asset Type *</InputLabel>
            <Select
              labelId="asset-type-label"
              id="asset-type-select"
              value={assetType}
              label="Asset Type *"
              onChange={(e) => setAssetType(e.target.value)}
            >
              {ASSET_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {typeError && <div className="error-text">Asset type is required</div>}
          </FormControl>
        </div>

        <div className="buttons-wrapper">
          <button className="btn btn-big btn-secondary" onClick={props.onClose}>
            Cancel
          </button>
          <button
            className={classNames('btn', 'btn-big', 'btn-main', {
              disabled: !assetNames.trim() || !assetType
            })}
            onClick={submit}
            disabled={loading}
          >
            Restore Assets
          </button>
        </div>
      </div>
      {loading && <AppLoading />}
    </div>
  );
}
